<template>
  <v-container fluid>
    <v-card>
      <v-toolbar dense style="background:#F5F5F5">
        <v-toolbar-title dense style="color:#607D8B;font-weight:bold">
          <v-icon color="#607D8B" class="mr-2">mdi-qrcode</v-icon>
          Mon QR Code
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- Bouton refresh -->
        <v-btn @click="refreshQRCode" :disabled="isRefreshing" icon>
          <v-icon
              color="#009688"
              large
              :class="{ 'rotate': isRefreshing }">
            mdi-refresh
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <!-- Chargement initial -->
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular size="70" color="#009688" indeterminate></v-progress-circular>
          <p class="mt-4">Génération de votre QR Code...</p>
        </div>

        <!-- Erreur -->
        <div v-else-if="error" class="text-center pa-8">
          <v-icon size="60" color="#F44336">mdi-alert-circle</v-icon>
          <h3 class="mt-4 red--text">{{ error }}</h3>
          <v-btn @click="refreshQRCode" color="#009688" class="mt-4">
            <v-icon left>mdi-refresh</v-icon>
            Réessayer
          </v-btn>
        </div>

        <!-- QR Code principal -->
        <div v-else>
          <!-- En-tête utilisateur -->
          <v-card outlined class="mb-4">
            <v-card-text class="text-center py-3">
              <v-avatar size="50" color="#009688" class="mb-2">
                <v-icon color="white">mdi-account</v-icon>
              </v-avatar>
              <h2 class="headline">{{ userInfo.nom }}</h2>
              <p class="subtitle-1 mb-0">Matricule: {{ userInfo.matricule }}</p>
            </v-card-text>
          </v-card>

          <!-- QR Code + Timer -->
          <v-row justify="center">
            <v-col cols="12" sm="8" md="6" lg="4">
              <v-card outlined elevation="4" class="text-center pa-4">
                <!-- QR Code -->
                <div class="qr-wrapper mb-4">
                  <img
                      v-if="qrImageUrl"
                      :src="qrImageUrl"
                      alt="QR Code"
                      class="qr-image"
                      @load="onQRImageLoad"
                      @error="onQRImageError">
                  <div v-else class="qr-placeholder">
                    <v-progress-circular indeterminate color="#009688"></v-progress-circular>
                    <p class="mt-2">Chargement QR...</p>
                  </div>
                </div>

                <!-- Timer -->
                <div class="timer-section">
                  <v-progress-circular
                      :value="timerPercentage"
                      :color="timerColor"
                      :size="80"
                      :width="8"
                      class="mb-2">
                    <span class="timer-text">{{ timeRemaining }}s</span>
                  </v-progress-circular>
                  <p class="subtitle-2 mb-0">Renouvellement automatique</p>

                  <!-- Bouton refresh manuel -->
                  <v-btn
                      @click="refreshQRCode"
                      :disabled="isRefreshing"
                      color="#009688"
                      outlined
                      small
                      class="mt-2">
                    <v-icon
                        left
                        small
                        :class="{ 'rotate': isRefreshing }">
                      mdi-refresh
                    </v-icon>
                    {{ isRefreshing ? 'Génération...' : 'Renouveler' }}
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Instructions -->
          <v-row justify="center" class="mt-4">
            <v-col cols="12" sm="10" md="8">
              <v-alert
                  outlined
                  type="info"
                  class="text-center">
                <strong>Présentez ce QR Code au terminal de paiement</strong>
              </v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js"

export default {
  name: "QRCodePayment",
  data() {
    return {
      qrGenerationTime: null,
      loading: true,
      error: null,
      isRefreshing: false,
      timeRemaining: 0,
      validitySeconds: 90,
      timerInterval: null,
      qrImageUrl: null,
      currentNonce: null,
      userInfo: {
        matricule: '',
        nom: ''
      }
    }
  },

  computed: {
    timerPercentage() {
      return (this.timeRemaining / this.validitySeconds) * 100
    },

    timerColor() {
      const percentage = this.timerPercentage
      if (percentage > 30) return '#4CAF50'
      if (percentage > 10) return '#FF9800'
      return '#F44336'
    }
  },

  async mounted() {
    this.loadUserInfo()
    await this.generateQRCode()

    // ✅ NOUVEAU: Écouter les événements de visibilité
    this.setupVisibilityListeners()
  },

  beforeDestroy() {
    this.clearTimer()
    // ✅ NOUVEAU: Nettoyer les listeners
    this.removeVisibilityListeners()
  },

  methods: {
    // ✅ MÉTHODE COMMUNE: Arrêt timer
    handlePageHidden() {
      console.log('👁️‍🗨️ Page/App cachée - Arrêt timer')
      this.clearTimer()
    },

    // Listeners directs vers méthodes communes
    setupVisibilityListeners() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('focus', this.handlePageVisible)      // ✅ DIRECT
      window.addEventListener('blur', this.handlePageHidden)        // ✅ DIRECT
      document.addEventListener('pause', this.handlePageHidden)     // ✅ DIRECT
      document.addEventListener('resume', this.handlePageVisible)   // ✅ DIRECT
    },

    removeVisibilityListeners() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      window.removeEventListener('focus', this.handlePageVisible)
      window.removeEventListener('blur', this.handlePageHidden)
      document.removeEventListener('pause', this.handlePageHidden)
      document.removeEventListener('resume', this.handlePageVisible)
    },

    // ✅ NOUVELLE MÉTHODE: Gestion changement de visibilité
    handleVisibilityChange() {
      if (document.hidden) {
        console.log('🙈 Page cachée - Arrêt timer')
        this.handlePageHidden()
      } else {
        console.log('👁️ Page visible - Régénération QR')
        this.handlePageVisible()
      }
    },

    // ✅ NOUVELLE MÉTHODE: Traitement page redevenue visible
    async handlePageVisible() {
      console.log('👁️ Page redevenue visible - RÉGÉNÉRATION FORCÉE du QR')

      // ✅ TOUJOURS régénérer, peu importe l'état
      await this.generateQRCode()
    },

    loadUserInfo() {
      const user = this.$store.getters.user
      console.log('DEBUG Store user:', user)
      console.log('DEBUG sessionStorage matricule:', sessionStorage.getItem('userId'))

      this.userInfo = {
        matricule: user.userId || sessionStorage.getItem('userId') || '',  // ✅ CORRIGÉ
        nom: `${user.accountFirstname || ''} ${user.accountLastname || ''}`.trim() || 'Utilisateur'
      }

      console.log('DEBUG userInfo final:', this.userInfo)
    },

    // ✅ MODIFIER: generateQRCode - Reset complet du timer
    async generateQRCode() {
      console.log('🔄 Début génération QR Code')

      // ✅ ARRÊTER IMMÉDIATEMENT tout timer existant
      this.clearTimer()

      // Si c'est un refresh manuel, utiliser isRefreshing
      if (!this.loading) {
        this.isRefreshing = true
      }

      this.error = null
      this.qrImageUrl = null
      // ✅ RESET immédiat des variables de timing
      this.timeRemaining = 0
      this.qrGenerationTime = null

      if (!this.userInfo.matricule) {
        this.error = 'Matricule non trouvé'
        this.loading = false
        this.isRefreshing = false
        return
      }

      try {
        console.log('📡 Appel API génération QR...')
        const response = await apiClient.post(
            `${this.$store.getters.user.baseUrl}/api/qr-payment/generate/${this.userInfo.matricule}`
        )

        if (response.data.status === 200 && response.data.datas.success) {
          const data = response.data.datas

          // Mettre à jour le nom si retourné par l'API
          if (data.qrToken.nomUtilisateur) {
            this.userInfo.nom = data.qrToken.nomUtilisateur
          }

          // ✅ NOUVEAU: Vérifier que le nonce est valide
          if (!data.qrData) {
            throw new Error('Nonce QR vide reçu du serveur')
          }

          // Stocker le nonce et générer l'URL Google Charts
          this.currentNonce = data.qrData

          // ✅ TIMESTAMP PRÉCIS de génération
          this.qrGenerationTime = Date.now()

          this.generateQRImageUrl(this.currentNonce)

          // ✅ RESET COMPLET du timer avec nouvelles valeurs
          this.timeRemaining = data.validitySeconds || 90
          this.validitySeconds = this.timeRemaining

          console.log(`✅ QR généré: ${this.currentNonce.substring(0, 8)}..., validité: ${this.timeRemaining}s`)

          // ✅ DÉMARRER timer avec valeurs fraîches
          this.startTimer()

          this.loading = false
          this.isRefreshing = false
        } else {
          this.error = response.data.message || 'Erreur lors de la génération'
          this.loading = false
          this.isRefreshing = false
        }
      } catch (error) {
        console.error('Erreur génération QR:', error)
        this.error = 'Impossible de générer le QR Code'
        this.loading = false
        this.isRefreshing = false
      }
    },

    /**
     * Générer l'URL du QR Code via Google Charts API
     */
    generateQRImageUrl(data) {
      const size = 300
      const encodedData = encodeURIComponent(data)
      this.qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedData}&margin=10`
    },

    /**
     * Fonction refresh manuelle
     */
    async refreshQRCode() {
      this.clearTimer()
      await this.generateQRCode()
    },

    /**
     * Gestion du chargement de l'image
     */
    onQRImageLoad() {
      console.log('QR Code image chargée avec succès')
    },

    /**
     * Gestion des erreurs de l'image
     */
    onQRImageError() {
      console.error('Erreur chargement image QR')
      this.error = 'Impossible de charger l\'image QR'
    },

    startTimer() {
      //console.log(`⏰ Démarrage timer: ${this.timeRemaining}s`)

      // ✅ S'assurer qu'aucun timer ne tourne
      this.clearTimer()

      // ✅ Ne démarrer que si timeRemaining valide
      if (this.timeRemaining <= 0) {
        console.log('⚠️ Timer non démarré: timeRemaining <= 0')
        return
      }

      this.timerInterval = setInterval(() => {
        this.timeRemaining--
        //console.log(`⏰ Timer: ${this.timeRemaining}s restantes`)

        if (this.timeRemaining <= 0) {
          console.log('⏰ Timer expiré - Régénération automatique')
          // Renouvellement automatique
          this.generateQRCode()
        }
      }, 1000)
    },

    clearTimer() {
      if (this.timerInterval) {
        console.log('🛑 Arrêt du timer')
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
  }
}
</script>

<style scoped>
.qr-image {
  max-width: 300px;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  background: white;
}

.qr-wrapper {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.qr-placeholder {
  width: 300px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
}

.timer-text {
  font-size: 18px;
  font-weight: bold;
  color: #424242;
}

.timer-section {
  margin-top: 16px;
}

/* Animation rotation pour l'icône refresh */
.rotate {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .qr-image {
    max-width: 250px;
    max-height: 250px;
  }

  .qr-placeholder {
    width: 250px;
    height: 250px;
  }

  .headline {
    font-size: 1.3rem !important;
  }
}
</style>